/**
 * Конкуренты бренда.
 *
 * Два признака, которые нельзя путать, и оба объявлены контрактом.
 *
 * * **Отслеживается** — доля голоса конкурента считается по **той же сетке**,
 *   что и своя. Иначе линии на графике несопоставимы (спека §8). Это же место
 *   упирается в тариф: число отслеживаемых ограничено планом.
 * * **Стоп-лист** — маркетплейс или агрегатор, попавший в ответ, конкурентом не
 *   является. Одновременно отслеживать и блокировать нельзя.
 *
 * Удаление сохраняет упоминания: у них обнуляется ссылка, а имя остаётся, —
 * иначе история разреза по конкурентам переписывается задним числом. Экран
 * говорит это прямо в подтверждении, а не прячет в справке.
 */

import { useState } from 'react';

import { api, type Page } from '../../api/client';
import { ApiError, fieldErrors } from '../../api/problem';
import { useAccess } from '../../session';
import { useLoad } from '../../ui/load';
import { Checkbox, Confirm, Switch, TextField } from '../../ui/fields';
import { Empty, ErrorNotice, Skeleton } from '../../ui/state';
import { competitorSource } from '../../ui/labels';
import { countOf, dateProse, domainProse } from '@shared/src/format';
import type { components } from '../../api/schema';

type Competitor = components['schemas']['Competitor'];
type Quotas = components['schemas']['OrganizationQuotas'];

export function Competitors({
  brandId,
  readOnly,
}: {
  brandId: string;
  readOnly: boolean;
}) {
  const access = useAccess();
  const list = useLoad<Page<Competitor>>(`competitors:${brandId}`, (signal) =>
    api.get<Page<Competitor>>(`/brands/${brandId}/competitors`, {
      signal,
      query: { limit: 100 },
    }),
  );
  const quotas = useLoad<Quotas | null>('quotas', (signal) =>
    access.reaches('GET', '/organizations/current/quotas')
      ? api.get<Quotas>('/organizations/current/quotas', { signal })
      : Promise.resolve(null),
  );

  const [name, setName] = useState('');
  const [domain, setDomain] = useState('');
  const [blocked, setBlocked] = useState(false);
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);
  const [removing, setRemoving] = useState<Competitor | null>(null);

  const problem = error instanceof ApiError ? error.problem : null;
  const fields = problem ? fieldErrors(problem) : {};
  const quota = quotas.state.kind === 'ready' ? quotas.state.data : null;

  async function add() {
    setBusy('new');
    setError(null);
    try {
      await api.post<Competitor>(`/brands/${brandId}/competitors`, {
        name: name.trim(),
        domain: domain.trim() || null,
        is_tracked: !blocked,
        is_blocked: blocked,
      });
      setName('');
      setDomain('');
      setBlocked(false);
      list.reload();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(null);
    }
  }

  async function patch(competitor: Competitor, body: Partial<Competitor>) {
    setBusy(competitor.id);
    setError(null);
    try {
      await api.patch<Competitor>(`/competitors/${competitor.id}`, body);
      list.reload();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(null);
    }
  }

  async function remove(competitor: Competitor) {
    setRemoving(null);
    setBusy(competitor.id);
    setError(null);
    try {
      await api.delete<void>(`/competitors/${competitor.id}`);
      list.reload();
    } catch (cause) {
      setError(cause);
    } finally {
      setBusy(null);
    }
  }

  if (list.state.kind === 'loading') return <Skeleton rows={3} />;
  if (list.state.kind === 'failed') {
    return (
      <ErrorNotice
        error={list.state.error}
        action={
          <button className="btn btn-ghost btn-sm" type="button" onClick={list.reload}>
            Повторить
          </button>
        }
      />
    );
  }

  const items = list.state.data.items;
  const tracked = items.filter((c) => c.is_tracked).length;
  const ceiling = quota?.max_competitors ?? null;
  const full = ceiling !== null && tracked >= ceiling;

  return (
    <>
      {error ? <ErrorNotice error={error} /> : null}

      {items.length === 0 ? (
        <Empty eyebrow="Конкуренты" title="Конкурентов ещё нет">
          Мы находим их сами: те, кого нейросети называют в ответах на ваши вопросы,
          появятся здесь после первого замера. До тех пор можно добавить очевидных руками.
        </Empty>
      ) : (
        <div className="tbl-wrap">
          <table className="mini">
            {/* Подпись называет таблицу — и только. Счёт компаний виден списком
                под ней, а сколько из них отслеживается — колонкой «Отслеживать»
                в каждой строке. */}
            <caption>Конкуренты бренда</caption>
            <thead>
              <tr>
                <th scope="col">Конкурент</th>
                <th scope="col">Откуда</th>
                <th scope="col">Последний раз в ответах</th>
                <th scope="col">Отслеживать</th>
                {/* «Стоп-лист» — наше слово: в форме рядом то же самое уже
                    названо по-человечески («Это не конкурент, а площадка»). */}
                <th scope="col">Не конкурент</th>
                {readOnly ? null : <th scope="col">&nbsp;</th>}
              </tr>
            </thead>
            <tbody>
              {items.map((competitor) => (
                <tr key={competitor.id}>
                  <td>
                    <span className="row-title">{competitor.name}</span>
                    <span className="stamp">
                      {competitor.domain
                        ? domainProse(competitor.domain)
                        : 'сайт не добавлен'}
                    </span>
                  </td>
                  <td>{competitorSource(competitor.source)}</td>
                  <td>
                    {competitor.last_seen_at ? (
                      dateProse(competitor.last_seen_at)
                    ) : (
                      <span className="dim">Ещё не встречался</span>
                    )}
                  </td>
                  <td>
                    {readOnly ? (
                      competitor.is_tracked ? (
                        'Да'
                      ) : (
                        <span className="dim">Нет</span>
                      )
                    ) : (
                      <Switch
                        label="Отслеживать конкурента"
                        checked={competitor.is_tracked}
                        busy={busy === competitor.id}
                        disabled={!competitor.is_tracked && full}
                        note={
                          !competitor.is_tracked && full
                            ? 'Достигнут лимит тарифа по отслеживаемым конкурентам'
                            : undefined
                        }
                        onChange={(next) =>
                          void patch(competitor, { is_tracked: next, is_blocked: false })
                        }
                      />
                    )}
                  </td>
                  <td>
                    {readOnly ? (
                      competitor.is_blocked ? (
                        'Да'
                      ) : (
                        <span className="dim">Нет</span>
                      )
                    ) : (
                      <Switch
                        label="Не считать конкурентом"
                        checked={competitor.is_blocked}
                        busy={busy === competitor.id}
                        onChange={(next) =>
                          void patch(competitor, { is_blocked: next, is_tracked: false })
                        }
                      />
                    )}
                  </td>
                  {readOnly ? null : (
                    <td>
                      <button
                        type="button"
                        className="linkish"
                        disabled={busy === competitor.id}
                        onClick={() => setRemoving(competitor)}
                      >
                        Убрать
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {full ? (
        <p className="stamp under-table warn">
          Тариф{quota?.plan_code ? '' : ''} даёт{' '}
          {countOf(
            ceiling ?? 0,
            'отслеживаемого конкурента',
            'отслеживаемых конкурента',
            'отслеживаемых конкурентов',
          )}
          , и все заняты. Снимите отслеживание с кого-то из списка или расширьте тариф.
          Остальные видны в списке, но в числа не входят.
        </p>
      ) : null}

      {readOnly ? null : (
        <div className="panel">
          <h2 className="panel-h">Добавить конкурента</h2>
          <form
            className="row-form"
            onSubmit={(event) => {
              event.preventDefault();
              void add();
            }}
          >
            <TextField
              label="Название"
              value={name}
              onChange={setName}
              error={
                fields.name ?? (problem?.code === 'conflict' ? problem.detail : null)
              }
              placeholder="Автоцентр Волга"
              hint="Так, как его называют в ответах нейросети"
            />
            <TextField
              label="Сайт"
              value={domain}
              onChange={setDomain}
              optional
              placeholder="volga-auto.ru"
              hint="По домену мы отличим тёзок"
            />
            <Checkbox
              label="Это не конкурент, а площадка"
              checked={blocked}
              onChange={setBlocked}
              note="Маркетплейс или агрегатор мы отложим в отдельный список и в разбор по конкурентам не возьмём"
            />
            <button
              className="btn btn-primary"
              type="submit"
              disabled={busy === 'new' || name.trim().length < 2 || (full && !blocked)}
            >
              Добавить конкурента
            </button>
          </form>
        </div>
      )}

      <Confirm
        open={removing !== null}
        title="Убрать конкурента"
        action="Убрать конкурента"
        danger
        onCancel={() => setRemoving(null)}
        onConfirm={() => removing && void remove(removing)}
      >
        <p>
          {removing ? `«${removing.name}» ` : ''}исчезнет из разбора по конкурентам. Уже
          найденные упоминания останутся в истории под своим именем — переписывать прошлые
          замеры мы не будем.
        </p>
      </Confirm>
    </>
  );
}
